import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { LanguageCode } from '@/lib/i18n';
import { useAuth } from './useAuth';
import { updateProfile } from '@/lib/api';

export function useLanguage() {
  const { i18n } = useTranslation();
  const { idToken } = useAuth();
  const [currentLang, setCurrentLang] = useState<LanguageCode>('en');

  useEffect(() => {
    setCurrentLang(i18n.language as LanguageCode);

    const handleLanguageChange = (lng: string) => {
      setCurrentLang(lng as LanguageCode);
      // Update document lang attribute
      document.documentElement.lang = lng;
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const changeLanguage = useCallback(
    async (lang: LanguageCode) => {
      // Change language locally (saves to localStorage via i18n detection)
      i18n.changeLanguage(lang);

      // Sync to profile for authenticated users
      if (idToken) {
        try {
          await updateProfile(idToken, { preferredLanguage: lang });
        } catch (error) {
          // Silently fail - localStorage still has the preference
          console.warn('Failed to sync language preference to profile:', error);
        }
      }
    },
    [i18n, idToken]
  );

  // Helper to apply language from profile (called by Dashboard after fetching profile)
  const applyProfileLanguage = useCallback(
    (preferredLanguage: string | undefined) => {
      if (preferredLanguage && preferredLanguage !== i18n.language) {
        i18n.changeLanguage(preferredLanguage);
      }
    },
    [i18n]
  );

  return { currentLang, changeLanguage, applyProfileLanguage };
}
